/**
 * RailAddGroup — "+ group" affordance appended to each group in the editable
 * Rail view (Slice 3B.4). Clicking shows an inline input; Enter/blur commits
 * `addWhereGroup(groupPath, value, connector, …)` (wraps the typed predicate in
 * a new `( … )` child group); Escape cancels. Analytics fired on commit.
 *
 * HAVING parity (Plan 3 Task 5): `kind` (default `'where'`) parameterizes the
 * testids and the `feature:vqe_${kind}_group_edit` analytics, and threads
 * `clause` into `addWhereGroup`.
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';

import type { WhereCombinator } from '../../../../sql-resolver/types';
import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { addWhereGroup } from '../../edit/edits/addWhereGroup';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';

export function RailAddGroup({
  groupPath,
  connector,
  editContext,
  kind = 'where',
}: {
  readonly groupPath: readonly number[];
  readonly connector: WhereCombinator;
  readonly editContext: VqeEditContext;
  readonly kind?: BooleanClauseTarget;
}): ReactElement {
  const pathKey = groupPath.join('-') || 'root';
  const { track } = useAnalytics();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const commit = (): void => {
    const trimmed = value.trim();
    setOpen(false);
    setValue('');
    if (trimmed.length === 0) return;
    void addWhereGroup(groupPath, trimmed, connector, toCommitCtx(editContext), kind);
    track(`feature:vqe_${kind}_group_edit`, { action: 'add-group' });
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid={`vqe-${kind}-rail-group-${pathKey}-add-group`}
        className="w-fit rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
        onClick={() => setOpen(true)}
      >
        + group
      </button>
    );
  }

  return (
    <input
      ref={inputRef}
      data-testid={`vqe-${kind}-rail-group-${pathKey}-add-group-input`}
      className="w-fit rounded-full border border-border bg-muted/40 px-2.5 py-1 font-mono text-[12px] outline-none"
      placeholder={`${connector} ( … )`}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
        if (e.key === 'Escape') {
          setValue('');
          setOpen(false);
        }
      }}
    />
  );
}
